import parse from "html-react-parser"
import React from "react"

import IconAlert from "@/components/global/icons/IconAlert"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from "@/components/ui/tooltip"
import { cn } from "@/lib/utils"

import IconCheck from "../global/icons/IconCheck"
import IconInfoCircle from "../global/icons/IconInfoCircle"
import IconLoading from "../global/icons/IconLoading"

type Props = {
  children: React.ReactNode
  isRadio?: boolean
  label: string | React.ReactNode
  description?: string
  error?: string | boolean
  displayIcons?: boolean
  className?: string
  labelClasses?: string
  icon?: React.ReactNode | null
  touched?: boolean
  dirty?: boolean
  valid?: boolean
  postfix?: string
  loading?: boolean
  tooltip?: string | null | React.ReactNode
  tooltipDisabled?: boolean
  tooltipTriggerColorClass?: string
}

const CustomTooltip = ({
  message,
  disabled = false,
  triggerIconClass = "text-purple"
}: {
  message: string | React.ReactNode
  triggerIconClass?: string
  disabled?: boolean
}) => (
  <TooltipProvider>
    <Tooltip>
      <TooltipTrigger>
        <IconInfoCircle
          className={cn(
            "text-purple transform -translate-y-[2px] w-6 h-6",
            triggerIconClass
          )}
        />
      </TooltipTrigger>
      {!disabled && <TooltipContent>{message}</TooltipContent>}
    </Tooltip>
  </TooltipProvider>
)

export const renderLabelContent = (
  label: string | React.ReactNode,
  tooltip?: string | React.ReactNode,
  tooltipTriggerColorClass?: string,
  tooltipDisabled?: boolean,
  isRadio?: boolean
) => {
  let output = null
  switch (typeof label) {
    case "string":
      output = (
        <>
          {parse(label)}
          {tooltip && (
            <CustomTooltip
              message={tooltip}
              disabled={tooltipDisabled}
              triggerIconClass={tooltipTriggerColorClass}
            />
          )}
        </>
      )
      break
    case "object":
      output = label
      break
  }

  return (
    <span
      className={cn("inline-flex items-center gap-2", {
        "mb-1": tooltip && !isRadio,
        "mb-2": !tooltip && !isRadio
      })}>
      {output}
    </span>
  )
}

const Field = ({
  children,
  isRadio,
  label,
  description,
  error,
  valid,
  touched,
  className,
  labelClasses,
  loading = false,
  postfix,
  tooltip,
  tooltipDisabled = false,
  tooltipTriggerColorClass = "text-purple",
  displayIcons = true,
  icon = null
}: Props) => {
  const childs = React.Children.toArray(children)
  const primaryChild = childs.filter((child) =>
    React.isValidElement(child)
  )[0] as React.ReactElement
  const id = getChildId(primaryChild)

  return (
    <>
      <div
        className={cn(className, {
          "has-error": touched && error,
          "radio-field-wrapper flex flex-row gap-2 items-center": isRadio
        })}>
        {isRadio && primaryChild}
        {label && (
          <label
            className={cn(
              "block cursor-pointer",
              {
                "text-base": isRadio,
                "text-sm": !isRadio
              },
              labelClasses
            )}
            htmlFor={id}>
            {renderLabelContent(
              label,
              tooltip,
              tooltipTriggerColorClass,
              tooltipDisabled,
              isRadio
            )}
          </label>
        )}
        {!isRadio && (
          <div className="relative">
            {!isRadio && primaryChild}
            {icon && (
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                {icon}
              </div>
            )}
            {touched && error && !loading && displayIcons && (
              <small className="error text-red absolute right-3 bottom-[21px] w-14 h-6 flex items-center justify-end bg-gradient-to-r from-transparent to-white">
                <IconAlert className="w-6 h-6 inline-block bg-white" />
              </small>
            )}
            {loading && displayIcons && (
              <small className="error text-red absolute right-3 bottom-[21px]">
                <IconLoading className="w-6 h-6 mr-1.5 inline-block" />
              </small>
            )}
            {valid && !loading && displayIcons && (
              <small className="error text-purple absolute right-3 bottom-[21px]">
                <IconCheck className="w-5 h-5 mr-1.5 inline-block" />
              </small>
            )}
            {postfix && !error && !loading && (
              <span
                className={cn(
                  "postfix absolute right-3 mb-0 text-black bottom-[1.25rem] text-sm",
                  touched && error && "text-red"
                )}>
                {postfix}
              </span>
            )}
          </div>
        )}
        {touched && error && typeof error === "string" && !isRadio && (
          <div className="text-red-500 text-sm mt-2">{error}</div>
        )}
        {description && <div className="text-black/60 mt-2">{description}</div>}
      </div>
      {touched && error && typeof error === "string" && isRadio && (
        <div className="text-red-500 text-sm mt-2">{error}</div>
      )}
    </>
  )
}

export { Field }

export const getChildId = (children: React.ReactNode) => {
  const child = React.Children.only(children)

  if (
    React.isValidElement<{
      id?: string
    }>(child)
  ) {
    if ("id" in child.props) {
      return (
        child.props as {
          id: string
        }
      ).id
    }
  }
  return undefined
}
